import { useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Cake, CreditCard, Mail, MapPin, Phone } from "lucide-react";
import { ageFromDob, initials } from "@clinic/ui";
import { isClinician } from "@clinic/types";
import { api } from "@/lib/api";
import { useMe } from "@/lib/auth";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PatientInfoTab } from "@/components/patient/PatientInfoTab";
import { EyeHistoryTab } from "@/components/patient/EyeHistoryTab";
import { PatientAppointmentsTab } from "@/components/patient/PatientAppointmentsTab";
import { DocumentsTab } from "@/components/patient/DocumentsTab";

export function PatientDetailPage() {
  const { patientId } = useParams({ strict: false }) as { patientId: string };
  const me = useMe();
  const patient = useQuery({
    queryKey: ["patient", patientId],
    queryFn: () => api.patients.get(patientId),
  });

  if (patient.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-28" />
        <Skeleton className="h-64" />
      </div>
    );
  }
  if (!patient.data) {
    return <p className="py-12 text-center text-text-tertiary">Patient not found.</p>;
  }
  const p = patient.data;

  return (
    <div>
      {/* header card */}
      <Card className="mb-6 flex flex-wrap items-center gap-5">
        <div className="flex h-16 w-16 items-center justify-center rounded-pill bg-accent-subtle/50 text-xl font-semibold text-accent-light">
          {initials(p.fullName)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-xl">{p.fullName}</h1>
            {p.archivedAt ? (
              <Badge tone="cancelled">Archived</Badge>
            ) : (
              <Badge tone="active">Active</Badge>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-text-secondary">
            <span className="flex items-center gap-1.5">
              <CreditCard size={14} /> <span className="font-mono text-xs">{p.idNumber}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <Cake size={14} /> {format(new Date(p.dob), "d MMM yyyy")} ({ageFromDob(p.dob)})
            </span>
            <span className="flex items-center gap-1.5">
              <Phone size={14} /> {p.phone}
            </span>
            {p.email && (
              <span className="flex items-center gap-1.5">
                <Mail size={14} /> {p.email}
              </span>
            )}
            {p.address && (
              <span className="flex items-center gap-1.5">
                <MapPin size={14} /> {p.address}
              </span>
            )}
          </div>
        </div>
      </Card>

      <Tabs defaultValue="history">
        <TabsList>
          <TabsTrigger value="info">Info</TabsTrigger>
          <TabsTrigger value="history">
            History ({p._count?.eyeRecords ?? 0})
          </TabsTrigger>
          <TabsTrigger value="appointments">
            Appointments ({p._count?.appointments ?? 0})
          </TabsTrigger>
          <TabsTrigger value="documents">Docs ({p._count?.documents ?? 0})</TabsTrigger>
        </TabsList>
        <TabsContent value="info">
          <PatientInfoTab patient={p} />
        </TabsContent>
        <TabsContent value="history">
          <EyeHistoryTab patientId={p.id} isDoctor={me.data ? isClinician(me.data.role) : false} />
        </TabsContent>
        <TabsContent value="appointments">
          <PatientAppointmentsTab patientId={p.id} />
        </TabsContent>
        <TabsContent value="documents">
          <DocumentsTab patientId={p.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
